import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useIntakeAssessmentApprovedSummary,
  useIntakeAssessmentList,
  INTAKE_ASSESSMENT_KIND_LABELS,
  INTAKE_ASSESSMENT_RECORD_KINDS,
  INTAKE_ASSESSMENT_STATUS_LABELS,
  type IntakeAssessmentRecordKind,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  canSeeIntakeAssessmentMenu,
  hasPermission,
} from '@/lib/intakeAssessmentPermissions';

interface Props {
  customerId: string;
}

export default function CustomerIntakeAssessmentPanel({ customerId }: Props) {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { summary, fetchSummary } = useIntakeAssessmentApprovedSummary();
  const { rows, fetchRecords } = useIntakeAssessmentList();

  const canRead = canSeeIntakeAssessmentMenu(permissions);
  const canCreate = hasPermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.CREATE,
  );

  useEffect(() => {
    if (!canRead || !customerId) return;
    void fetchSummary(customerId);
    void fetchRecords({ customerId, page: 1, limit: 50 });
  }, [canRead, customerId, fetchSummary, fetchRecords]);

  if (!canRead) return null;

  const approvedByKind: Record<IntakeAssessmentRecordKind, boolean> = {
    golden_analysis: Boolean(summary?.goldenAnalysis),
    deal_intention: Boolean(summary?.dealIntention),
    health_assessment: Boolean(summary?.healthAssessment),
  };

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">接诊评估 / 黄金分析 / 成交意向</h3>
        {canCreate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              navigate(
                `/intake-assessment/records/create?customerId=${encodeURIComponent(customerId)}`,
              )
            }
          >
            新建记录
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
        {INTAKE_ASSESSMENT_RECORD_KINDS.map((kind) => {
          const ok = approvedByKind[kind];
          const entry =
            kind === 'golden_analysis'
              ? summary?.goldenAnalysis
              : kind === 'deal_intention'
                ? summary?.dealIntention
                : summary?.healthAssessment;
          return (
            <div
              key={kind}
              className={`rounded-md border p-3 ${
                ok ? 'border-green-200 bg-green-50' : 'border-zinc-200 bg-zinc-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {INTAKE_ASSESSMENT_KIND_LABELS[kind]}
                </span>
                <span
                  className={`text-xs ${
                    ok ? 'text-green-800' : 'text-muted-foreground'
                  }`}
                >
                  {ok ? '已批准' : '尚未批准'}
                </span>
              </div>
              {entry && (
                <>
                  <p className="mt-2 line-clamp-3 text-xs text-green-900">
                    {entry.approvedSummary}
                  </p>
                  <button
                    className="mt-2 text-xs text-primary hover:underline"
                    onClick={() =>
                      navigate(`/intake-assessment/records/${entry.recordId}`)
                    }
                  >
                    查看详情 →
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {rows.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium">该客户所有记录</h4>
          <ul className="space-y-1 text-xs">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between rounded-md border px-2 py-1"
              >
                <span>
                  {INTAKE_ASSESSMENT_KIND_LABELS[row.recordKind]} · {row.title}
                </span>
                <span className="flex items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px]">
                    {INTAKE_ASSESSMENT_STATUS_LABELS[row.status]}
                  </span>
                  <button
                    className="text-primary hover:underline"
                    onClick={() =>
                      navigate(`/intake-assessment/records/${row.id}`)
                    }
                  >
                    打开
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
